import React from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { PDFViewer } from 'components/PDFViewer'
import { SocialShareDrawer } from 'components/SocialShareDrawer'
import AndroidHandler from 'utils/androidHandler'
import { generateDownloadFile } from 'utils/download'
import {
  SHARE_TEXT,
  DOWNLOAD_TOAST_MESSAGE,
} from 'app/DocumentCollectionPage/constants'
import { useAPIDocumentCollectionDocument } from 'api/document_collection/useAPIDocumentCollection'
import { useSETrackEvent } from 'app/DocumentCollectionPage/hooks/useSETrackEvent'

interface IDocumentPreviewPanelProps {
  id: string
  parentCategory?: string
}

const DocumentPreviewPanel = ({
  id,
  parentCategory,
}: IDocumentPreviewPanelProps) => {
  const { data, isLoading } = useAPIDocumentCollectionDocument(id)
  const document = data?.data

  const androidHandler = AndroidHandler()
  const toast = useToast()
  const { SETrackDocumentLandingPage } = useSETrackEvent()

  const [socialShareDrawerOpen, setSocialShareDrawerOpen] =
    React.useState<boolean>(false)

  const shareUrl = React.useMemo(
    () => `${process.env.NEXT_PUBLIC_BASE_URL}/dokumen/${document?.id}`,
    [document?.id]
  )

  const shareHandler = () => {
    SETrackDocumentLandingPage('CLICK_KUMPULAN_DOKUMEN_SHARE', {
      screen_name: 'KUMPULAN_DOKUMEN_DOCUMENT_PAGE',
      event_trigger: 'KUMPULAN_DOKUMEN_DOCUMENT_PAGE',
      parent_category: parentCategory,
      category: document?.categoryName,
      title: document?.title,
      content_id: document?.id,
      url: shareUrl,
    })
    if (androidHandler.enabled) {
      androidHandler.shareToolkitUrl(`${SHARE_TEXT} ${shareUrl}`)
    } else {
      setSocialShareDrawerOpen(true)
    }
  }

  const downloadHandler = () => {
    const categoryName = document?.categoryName ?? ''
    const documentUrl = document?.documentUrl ?? ''
    const documentName = documentUrl.substring(documentUrl.lastIndexOf('/') + 1)

    if (!Boolean(documentUrl)) {
      return
    }

    SETrackDocumentLandingPage('CLICK_KUMPULAN_DOKUMEN_DOWNLOAD', {
      screen_name: 'KUMPULAN_DOKUMEN_DOCUMENT_PAGE',
      event_trigger: 'KUMPULAN_DOKUMEN_DOCUMENT_PAGE',
      parent_category: parentCategory,
      category: document?.categoryName,
      title: document?.title,
      content_id: document?.id,
      url: documentUrl,
    })

    try {
      if (androidHandler.enabled) {
        androidHandler.downloadCollection({
          path: `${categoryName}/`,
          url: documentUrl,
          toast: DOWNLOAD_TOAST_MESSAGE,
        })
      } else {
        generateDownloadFile(document?.documentUrl, documentName)
        toast({
          message: DOWNLOAD_TOAST_MESSAGE,
        })
      }
    } catch (e) {}
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Shimmer height="30px" width="60%" style={{ borderRadius: 8 }} />
        <Shimmer
          height="calc(100vh - 200px)"
          width="100%"
          style={{ borderRadius: 8 }}
        />
      </div>
    )
  }

  if (!document?.documentUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Text variant="body" color="subdued">
          Dokumen tidak tersedia
        </Text>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b bg-white sticky top-0 z-10">
        <Text variant="heading-sm" className="line-clamp-1">
          {document?.title}
        </Text>
      </div>

      <div
        key={document?.documentUrl}
        className="flex-1 overflow-auto w-full relative pb-4"
      >
        <PDFViewer url={document?.documentUrl} />
      </div>

      <div className="sticky bottom-0 bg-white border-t p-4">
        <div className="flex justify-around space-x-2">
          <Button color="white" fullWidth onClick={shareHandler}>
            <Icon>share</Icon>&nbsp;Bagikan
          </Button>
          <Button color="blue" fullWidth onClick={downloadHandler}>
            <Icon color="inverse">download</Icon>&nbsp;Unduh
          </Button>
        </div>
      </div>

      {socialShareDrawerOpen && (
        <SocialShareDrawer
          isOpen={socialShareDrawerOpen}
          onClose={() => {
            setSocialShareDrawerOpen(false)
          }}
          shareUrl={shareUrl}
          shareText={SHARE_TEXT}
        />
      )}
    </div>
  )
}

export default DocumentPreviewPanel
